import { useCallback, useEffect, useRef } from 'react';
import { Tldraw, Editor, TLRecord } from 'tldraw';
import 'tldraw/tldraw.css';
import * as Y from 'yjs';
import { Socket } from 'socket.io-client';
import { bindYDocToRoom } from './room-yjs';

interface WhiteboardProps {
  socket: Socket;
  theme: 'dark' | 'light';
}

/**
 * A tldraw canvas shared over the room. We let tldraw create and own its store
 * (so all base records exist) and wire the Yjs sync in onMount:
 *   - local document edits are pushed to a Yjs Y.Map (relayed to the room)
 *   - remote Yjs changes are applied granularly back into the store
 * Only document-scoped records (shapes/pages) are shared; per-user UI state
 * (camera, selection) stays local.
 */
const Whiteboard = ({ socket, theme }: WhiteboardProps) => {
  const editorRef = useRef<Editor | null>(null);
  const themeRef = useRef(theme);

  // Live-switch tldraw's color scheme when the session theme changes.
  useEffect(() => {
    themeRef.current = theme;
    editorRef.current?.user.updateUserPreferences({ colorScheme: theme });
  }, [theme]);

  const handleMount = useCallback(
    (editor: Editor) => {
      editorRef.current = editor;
      editor.user.updateUserPreferences({ colorScheme: themeRef.current });

      const store = editor.store;
      const ydoc = new Y.Doc();
      const yStore = ydoc.getMap<TLRecord>('tldraw');

      const unbind = bindYDocToRoom(socket, 'whiteboard', ydoc);

      // Local edits -> Yjs.
      const unlisten = store.listen(
        (entry) => {
          const { added, updated, removed } = entry.changes;
          ydoc.transact(() => {
            Object.values(added).forEach((r) => yStore.set(r.id, r));
            Object.values(updated).forEach(([, r]) => yStore.set(r.id, r));
            Object.values(removed).forEach((r) => yStore.delete(r.id));
          });
        },
        { source: 'user', scope: 'document' }
      );

      // Remote Yjs changes -> store (apply only the keys that changed).
      const observer = (events: Y.YEvent<any>[], txn: Y.Transaction) => {
        if (txn.local) return;
        store.mergeRemoteChanges(() => {
          events.forEach((event) => {
            event.changes.keys.forEach((change, id) => {
              if (change.action === 'delete') {
                store.remove([id as any]);
              } else {
                const record = yStore.get(id);
                if (record) store.put([record]);
              }
            });
          });
        });
      };
      yStore.observeDeep(observer);

      // Reconcile only AFTER the server's initial sync arrives, so two clients
      // don't both seed an empty board and diverge onto different pages.
      // bindYDocToRoom already applied the sync into yStore (and the observer
      // loaded it into the store); we only seed if the room is still empty
      // afterwards — i.e. we're genuinely the first participant.
      let reconciled = false;
      const reconcile = ({ docKey }: { docKey: string }) => {
        if (docKey !== 'whiteboard' || reconciled) return;
        reconciled = true;
        if (yStore.size === 0) {
          const docRecords = store.serialize('document');
          ydoc.transact(() => {
            Object.entries(docRecords).forEach(([id, record]) =>
              yStore.set(id, record as TLRecord)
            );
          });
        }
      };
      socket.on('room:sync', reconcile);

      return () => {
        socket.off('room:sync', reconcile);
        unlisten();
        yStore.unobserveDeep(observer);
        unbind();
        ydoc.destroy();
      };
    },
    [socket]
  );

  return (
    <div className="h-full w-full">
      {/*
       * licenseKey is optional. Without one, tldraw runs on its free tier:
       * it renders fully and adds a small "made with tldraw" watermark, and
       * logs a "license required" notice in production (a warning, not a
       * block). Set REACT_APP_TLDRAW_LICENSE_KEY only if you buy a business
       * license to remove the watermark.
       */}
      <Tldraw
        licenseKey={process.env.REACT_APP_TLDRAW_LICENSE_KEY}
        onMount={handleMount}
      />
    </div>
  );
};

export default Whiteboard;
